import React from 'react';
import { Box, Loading, SystemText, SaveButton, DebugInfo } from 'e4shared';
import { useDispatch, useSelector } from 'react-redux';

import { setAsideFilters, setShowSuccess, toggleFilterAside } from 'actions/ui';
import { apiPost, apiDownloadFile } from 'services';

import NoResults from 'components/general/noResults';
import PageHeader from 'components/general/pageHeader';
import SearchField from 'components/general/searchField';

import ManageTask from '../components/manageTask';
import TaskList from './components/taskList';

import posthog from 'posthog-js';
import { hasModule } from 'helpers/profile';

export const TaskTracker = ({}) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(true);
    const [searchFilter, setSearchFilter] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState('name');
    const [descending, setDescending] = React.useState(false);
    const [recordCount, setRecordCount] = React.useState(0);
    const [data, setData] = React.useState(null);
    const [showAssign, setShowAssign] = React.useState(false);
    const [downloadingReport, setDownloadingReport] = React.useState(false);

    const { filterAsideOpen, asideFilters } = useSelector(state => state.ui);
    const user = useSelector(state => state.user.user);
    const allowCoaches = user.companySettings.development.allowCoaches;

    const api = {
        loadObjectiveDashboard: async callback => {
            setLoading(true);
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            const qs = `?page=${page}&sort=${sortBy}&desc=${descending}`;

            await apiPost(
                `task-dashboard${qs}`,
                filters,
                user.guid,
                response => {
                    setData(response);
                    setRecordCount(response.recordCount);
                    callback?.();
                    setLoading(false);
                },
                null,
                'POST',
                'json'
            );
        },
        assign: (details, callback) => {
            apiPost('review-assign', details, user.guid, () => {
                dispatch(setShowSuccess(SystemText('ObjectiveTracker/Tabs/Reviews/Success')));
                setShowAssign(false);
                callback();
                api.loadObjectiveDashboard();
            });
        },
        report: () => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            apiDownloadFile(
                'task-report',
                filters,
                user.guid,
                () => {
                    setDownloadingReport(false);
                    posthog.capture('tracker', { trackerType: 'TaskTracker', userEvent: 'export' });
                },
                () => {
                    //re-enable the button if the download fails
                    setDownloadingReport(false);
                },
                'POST'
            );
        },
    };

    React.useEffect(() => {
        api.loadObjectiveDashboard();
    }, []);

    React.useEffect(() => {
        let fields = [
            {
                property: 'type',
                name: SystemText('ObjectiveTracker/Filters/Type'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'S', name: SystemText('ObjectiveTracker/Filters/Type/Standard') },
                    { id: 'V', name: SystemText('ObjectiveTracker/Filters/Type/Video') },
                    { id: 'M', name: SystemText('ObjectiveTracker/Filters/Type/Metric') },
                    { id: 'L', name: SystemText('ObjectiveTracker/Filters/Type/Learning') },
                ],
            },
            {
                property: 'date_Assigned',
                name: SystemText('ObjectiveTracker/Filters/Assigned'),
                type: 'range',
            },
            {
                property: 'date_Due',
                name: SystemText('ObjectiveTracker/Filters/Due'),
                type: 'range',
            },
            {
                property: 'statuses',
                name: SystemText('ObjectiveTracker/Filters/Status'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'L', name: SystemText('ObjectiveTracker/Filters/Status/Overdue') },
                    { id: 'O', name: SystemText('ObjectiveTracker/Filters/Status/Outstanding') },
                    { id: 'A', name: SystemText('ObjectiveTracker/Filters/Status/Awaiting') },
                    { id: 'C', name: SystemText('ObjectiveTracker/Filters/Status/Complete') },
                ],
            },
            {
                property: 'manager_Ids',
                name: SystemText('ObjectiveTracker/Filters/Manager'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'coach_Ids',
                name: SystemText('ObjectiveTracker/Filters/Coach'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'role_Ids',
                name: SystemText('ObjectiveTracker/Filters/Role'),
                type: 'list',
            },
            {
                property: 'job_Title',
                name: SystemText('ObjectiveTracker/Filters/Job'),
                type: 'text',
            },
            {
                property: 'department_Ids',
                name: SystemText('ObjectiveTracker/Filters/Department'),
                type: 'list',
            },
            {
                property: 'business_Unit_Ids',
                name: SystemText('ObjectiveTracker/Filters/BusinessUnit'),
                type: 'list',
            },
            {
                property: 'location_Ids',
                name: SystemText('ObjectiveTracker/Filters/Location'),
                type: 'list',
            },
            {
                property: 'language_Ids',
                name: SystemText('ObjectiveTracker/Filters/Language'),
                type: 'list',
            },
            {
                property: 'owner_Ids',
                name: SystemText('ObjectiveTracker/Filters/Owner'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'competency_Ids',
                name: SystemText('ObjectiveTracker/Filters/Behavior'),
                type: 'list',
                nameProperty: 'full_Name',
            },
        ];

        if (!allowCoaches) {
            fields = fields.filter(x => x.property !== 'coach_Ids');
        }

        dispatch(setAsideFilters(fields));
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'TaskTracker');
        posthog.capture('tracker', { trackerType: 'TaskTracker', userEvent: 'view' });
    }, []);

    React.useEffect(() => {
        if (!hasModule(user, 'objectives')) {
            history.replace('/forbidden');
        }
    }, [user]);

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    React.useEffect(() => {
        if (!filterAsideOpen) {
            setPage(1);
            api.loadObjectiveDashboard();
        }
    }, [filterAsideOpen]);

    React.useEffect(() => {
        setPage(1);
        api.loadObjectiveDashboard();
    }, [searchFilter, sortBy, descending]);

    React.useEffect(() => {
        api.loadObjectiveDashboard();
    }, [page]);

    const extraButtons = () => (
        <button className="btn btn-success" onClick={() => setShowAssign(true)}>
            <i className="mr10 fa fa-plus" />
            {SystemText('ObjectiveTracker/Buttons/Add')}
        </button>
    );

    const showFilterMenu = () => {
        dispatch(toggleFilterAside(true));
    };

    return (
        <div>
            <PageHeader title={SystemText('ObjectiveTracker/Title')} extra={extraButtons} tracker />
            <section className="content mtm10 tracker" data-testid="task-tracker-content">
                <Box>
                    <div className="mb10">
                        <SearchField
                            value={searchFilter}
                            onChange={setSearchFilter}
                            placeholder={SystemText('ObjectiveTracker/Search')}
                        >
                            {(user.administrator || user.isManager) && (
                                <SaveButton
                                    className="btn btn-info mr10"
                                    saving={downloadingReport}
                                    setSaving={setDownloadingReport}
                                    onClick={api.report}
                                >
                                    <i className="fa fa-download mr5" />
                                    {SystemText('ObjectiveTracker/Buttons/Report')}
                                </SaveButton>
                            )}
                            <button className="btn btn-success" id="search-filter-button" onClick={showFilterMenu}>
                                <i className="fa fa-bars-filter mr5" id="search-filter-button-icon" />
                                {SystemText('ObjectiveTracker/Buttons/Filter')}
                            </button>
                        </SearchField>
                    </div>
                    <div>
                        {loading ? (
                            <Loading />
                        ) : recordCount ? (
                            <TaskList
                                reload={api.loadObjectiveDashboard}
                                tasks={data.data}
                                page={page}
                                setPage={setPage}
                                recordCount={recordCount}
                                {...sortProps}
                            />
                        ) : (
                            <NoResults
                                title={SystemText('ObjectiveTracker/NoResults/Title')}
                                message={SystemText('ObjectiveTracker/NoResults/Message')}
                            />
                        )}
                    </div>
                </Box>
            </section>
            <ManageTask
                values={{ owner_Id: user.id }}
                show={showAssign}
                reload={api.loadObjectiveDashboard}
                close={() => setShowAssign(false)}
            />
        </div>
    );
};

export default TaskTracker;
